import React, { useMemo } from "react";
import { Img, Heading, Text } from "../../components";
import { useNavigate } from "react-router-dom";

/** helpers */
const pick = (obj, paths, fallback = null) => {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = obj;
    let ok = true;
    for (const k of parts) {
      if (cur && Object.prototype.hasOwnProperty.call(cur, k)) cur = cur[k];
      else {
        ok = false;
        break;
      }
    }
    if (ok && cur !== null && cur !== undefined && cur !== "") return cur;
  }
  return fallback;
};

const parseNumber = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).replace(/[^\d.-]/g, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

const formatMoney = (val) => {
  const n = parseNumber(val);
  if (n === null) return "---";
  return `₪ ${n.toLocaleString("he-IL")}`;
};

const isAuctionNotification = (n) =>
  Number(pick(n, ["is_auction", "car.is_auction", "data.is_auction"], 0)) === 1 ||
  String(pick(n, ["type", "notification_type", "type_text"], "")).startsWith("auction_");

const isWinnerNotification = (n) => {
  const type = String(pick(n, ["type", "notification_type", "type_text"], ""));
  return type === "offer_approved" || type === "immediate_purchase_approved";
};

const isRejectedNotification = (n) => {
  const type = String(pick(n, ["type", "notification_type", "type_text"], ""));
  return (
    type === "offer_approved_other" ||
    type === "immediate_purchase_closed_other" ||
    type === "auction_loser_after_winner_selected"
  );
};

const getMyOfferVal = (n) =>
  pick(
    n,
    [
      "my_offer_price",
      "my_price",
      "offered_price",
      "offer_price",
      "data.my_offer_price",
      "data.offered_price",
    ],
    null
  );

const getApprovedOfferVal = (n) => {
  const rawType = String(pick(n, ["type", "notification_type", "type_text"], "")).toLowerCase();
  const isApprovedOther =
    rawType.includes("offer_approved_other") ||
    Number(pick(n, ["offer_approved_other", "data.offer_approved_other"], 0)) === 1;

  if (isApprovedOther) {
    return pick(
      n,
      [
        "approved_other_price",
        "winner_offer_price",
        "approved_offer_price",
        "approved_price",
        "accepted_price",
        "data.approved_other_price",
        "data.winner_offer_price",
        "data.approved_price",
      ],
      null
    );
  }

  return pick(
    n,
    [
      "approved_offer_price",
      "approved_price",
      "accepted_price",
      "winner_offer_price",
      "data.approved_offer_price",
      "data.approved_price",
      "data.accepted_price",
      // fallback אם אין שדה ייעודי
      "my_offer_price",
      "offered_price",
      "offer_price",
      "data.my_offer_price",
      "data.offered_price",
    ],
    null
  );
};

const getCarIdFromNotif = (n) =>
  pick(
    n,
    ["car_id", "ad_id", "entity_id", "object_id", "data.car_id", "data.ad_id"],
    null
  );

const SeenBadge = ({ isRead, onClick }) => {
  const bg = isRead ? "rgb(237, 255, 216)" : "rgb(255, 233, 233)";
  const clr = isRead ? "rgb(119, 172, 59)" : "rgb(214, 31, 31)";

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-[30px] h-[30px] rounded-[8px] flex items-center justify-center cursor-pointer transition"
      style={{ backgroundColor: bg }}
      title={isRead ? "נצפה" : "סמן כנצפה"}
      aria-label={isRead ? "נצפה" : "סמן כנצפה"}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path
          d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z"
          stroke={clr}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="12" r="2.5" stroke={clr} strokeWidth="2" />
      </svg>
    </button>
  );
};

export default function NotificationCard({ notification, onClose }) {
  const navigate = useNavigate();

  const {
    manufacturer,
    model,
    sub_model,
    year,
    type,
  } = notification;

  const isRead = Number(pick(notification, ["is_read", "read", "data.is_read"], 0)) === 1;

  // שם רכב
  const carDisplay = [manufacturer, model, sub_model, year].filter(Boolean).join(" ");

  const priceRows = useMemo(() => {
    if (isAuctionNotification(notification)) {
      if (isWinnerNotification(notification)) {
        return [{ label: "מחיר הזכיה שלך", value: getMyOfferVal(notification) }];
      }
      if (isRejectedNotification(notification)) {
        return [
          { label: "מחיר הזכיה", value: getApprovedOfferVal(notification) },
          { label: "מחיר ההצעה שלך", value: getMyOfferVal(notification) },
        ];
      }
      return [{ label: "מחיר ההצעה", value: getMyOfferVal(notification) }];
    }

    return [
      { label: "מחיר הצעה שלי", value: getMyOfferVal(notification) },
      { label: "מחיר הצעה שאושרה", value: getApprovedOfferVal(notification) },
    ];
  }, [notification]);

  const titles = {
    offer_approved: isAuctionNotification(notification) ? "!זכית במכרז" : "!זכית במודעת רכב",
    offer_approved_other: "הצעה אחרת אושרה",
    new_offer: isAuctionNotification(notification) ? "הצעה חדשה במכרז" : "!קיבלת הצעה חדשה",
    immediate_purchase_approved: "רכישה מידית",
    immediate_purchase_closed_other: "ההצעה שלך נדחתה",
    auction_created: "עלה מכרז חדש",
    auction_starts_tomorrow: "הרכב עולה למכרז מחר",
    auction_starts_in_one_hour: "מכרז מתחיל בעוד שעה",
    auction_loser_after_winner_selected: "המכרז הסתיים ונבחר זוכה אחר",
  };

  const carId = getCarIdFromNotif(notification);

  const markRead = async () => {
    if (isRead) return;
    if (onClose) await onClose(); // זה אצלך עושה mark-read + עדכון UI
  };

  const openAd = async () => {
    if (!isRead) await markRead();
    if (carId) navigate(`/dealer-cars/${carId}`);
  };

  return (
    <>
      {/* ✅ במקום X: עין + סימון נקראה */}
      <div className="absolute top-3 left-3">
        <SeenBadge isRead={isRead} onClick={markRead} />
      </div>

      <div className="flex items-start gap-4 flex-row-reverse text-right">
        <Img src="/images/gold-cup.svg" alt="גביע" className="w-20 h-20" />

        <div className="flex-1 flex flex-col text-right">
          <Heading as="h6" className="font-bold text-red-600 mb-1">
            {titles[type] ?? "התראה"}
          </Heading>

          {/* רכב */}
          {carDisplay && (
            <div className="flex items-center justify-end gap-1">
              <Text className="font-medium text-gray-900">{carDisplay}</Text>
              <Text className="text-gray-700">:רכב</Text>
            </div>
          )}

          {priceRows.map((row) => (
            <div key={row.label} className="flex items-center justify-end gap-1 mt-1">
              <Text className="font-medium text-gray-900">{formatMoney(row.value)}</Text>
              <Text className="text-gray-700">{row.label}:</Text>
            </div>
          ))}

          {/* ✅ לצפיה במודעה */}
          {!!carId && (
            <div className="flex justify-end mt-3">
              <button
                type="button"
                onClick={openAd}
                className="flex items-center gap-2 rounded-[10px] px-3 py-2 bg-white border border-gray-200"
              >     <img
                  className="h-[12px]"
                  src="/images/img_settings_black_900.svg"
                  alt="Settings"
                />
                <span className="text-black-900_03 font-assistant text-[14px] font-semibold">
                  לצפיה במודעה
                </span>
           
              </button>
            </div>
          )}

  
        </div>
      </div>
    </>
  );
}
